import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Boxes,
  CheckCircle2,
  ChevronRight,
  Gift,
  Headphones,
  LayoutGrid,
  LogIn,
  Mail,
  MapPin,
  Menu,
  MessageSquare,
  Phone,
  Receipt,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Tag,
  Truck,
  Users,
  UtensilsCrossed,
  Wallet,
  WifiOff,
  Wrench,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button, Logo } from "@/components/ui";

export interface LandingScreenProps {
  onGoToLogin?: (params?: { tenant?: string }) => void;
}

export function LandingScreen({ onGoToLogin }: LandingScreenProps) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLoginClick = () => {
    if (onGoToLogin) {
      onGoToLogin();
    } else {
      void navigate({ to: "/login" });
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0f12] text-white flex flex-col selection:bg-electric selection:text-white">
      {/* ------------------------------------------------------------------------- */}
      {/* Barra de Navegación Superior */}
      {/* ------------------------------------------------------------------------- */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0c0f12]/90 backdrop-blur-md px-4 sm:px-6 py-3.5 sm:py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <Logo variant="isotipo" tone="white" className="h-8 sm:h-9" />
            <Logo variant="wordmark" tone="white" className="h-4.5 sm:h-5" alt="MegaSyR" />
          </div>

          <nav className="hidden md:flex items-center gap-6 text-sm text-white/70">
            <a href="#soluciones" className="hover:text-white transition-colors">
              Soluciones
            </a>
            <a href="#modulos" className="hover:text-white transition-colors">
              Módulos
            </a>
            <a href="#offline" className="hover:text-white transition-colors">
              Tecnología Offline
            </a>
            <a href="#prueba-gratis" className="text-cyan-300 font-semibold hover:text-white transition-colors">
              Prueba Gratis
            </a>
            <a href="#contacto" className="hover:text-white transition-colors">
              Contacto
            </a>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void navigate({ to: "/contacto" })}
              className="hidden sm:inline-flex text-xs sm:text-sm border-white/20 text-white hover:bg-white/10"
            >
              <Gift className="size-3.5 sm:size-4 text-cyan-400" />
              <span>Prueba Gratis</span>
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={handleLoginClick}
              className="flex items-center gap-1.5 shadow-md shadow-electric/20 text-xs sm:text-sm px-2.5 sm:px-3"
            >
              <LogIn className="size-3.5 sm:size-4" />
              <span>Iniciar Sesión</span>
            </Button>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={mobileMenuOpen ? "Cerrar menú principal" : "Abrir menú principal"}
              className="md:hidden flex items-center justify-center size-9 rounded-msr border border-white/15 bg-white/5 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>

        {/* Menú desplegable para móviles */}
        {mobileMenuOpen ? (
          <div className="md:hidden mt-3 pt-3 border-t border-white/10 flex flex-col gap-1 animate-in fade-in slide-in-from-top-2 duration-200">
            <a
              href="#soluciones"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3.5 py-2.5 rounded-msr text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-between"
            >
              <span>Soluciones por Giro</span>
              <ChevronRight className="size-4 text-white/40" />
            </a>
            <a
              href="#modulos"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3.5 py-2.5 rounded-msr text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-between"
            >
              <span>Módulos y Herramientas</span>
              <ChevronRight className="size-4 text-white/40" />
            </a>
            <a
              href="#offline"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3.5 py-2.5 rounded-msr text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-between"
            >
              <span>Tecnología Offline</span>
              <ChevronRight className="size-4 text-white/40" />
            </a>
            <a
              href="#prueba-gratis"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3.5 py-2.5 rounded-msr text-sm font-semibold text-cyan-300 hover:text-white hover:bg-cyan-500/10 transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Gift className="size-4 text-cyan-400" />
                <span>Prueba Gratis 15 Días</span>
              </span>
              <ChevronRight className="size-4 text-cyan-400" />
            </a>
            <a
              href="#contacto"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3.5 py-2.5 rounded-msr text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-between"
            >
              <span>Contacto y Soporte</span>
              <ChevronRight className="size-4 text-white/40" />
            </a>

            <div className="pt-2.5 mt-1 border-t border-white/10 flex flex-col gap-2">
              <Button
                variant="primary"
                size="md"
                fullWidth
                onClick={() => {
                  setMobileMenuOpen(false);
                  void navigate({ to: "/contacto" });
                }}
                className="flex items-center justify-center gap-2 py-3"
              >
                <Gift className="size-4 text-cyan-300" />
                <span>Pedir Cuenta de Prueba Gratis</span>
              </Button>

              <a
                href="https://wa.me/528672131335?text=Hola,%20quisiera%20informaci%C3%B3n%20sobre%20MegaSyR"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-msr border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-xs font-semibold text-emerald-400 hover:bg-emerald-500/20 transition-colors"
              >
                <MessageSquare className="size-3.5" />
                <span>WhatsApp Directo: (867) 213-1335</span>
              </a>
            </div>
          </div>
        ) : null}
      </header>

      <main className="flex-1">
        {/* ------------------------------------------------------------------------- */}
        {/* Hero Section */}
        {/* ------------------------------------------------------------------------- */}
        <section className="relative overflow-hidden pt-12 pb-16 sm:pt-20 sm:pb-28 lg:pt-24 lg:pb-32 px-4 sm:px-6">
          {/* Resplandor decorativo de fondo */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 transform-gpu overflow-hidden blur-3xl opacity-35"
          >
            <div className="aspect-[1155/678] w-[72rem] bg-gradient-to-tr from-electric via-cyan-400 to-indigo-600 [clip-path:polygon(74.1%_44.1%,100%_61.6%,97.5%_26.9%,85.5%_0.1%,80.7%_2%,72.5%_32.5%,60.2%_62.4%,52.4%_68.1%,47.5%_58.3%,45.2%_34.5%,27.5%_76.7%,0.1%_64.9%,17.9%_100%,27.6%_76.8%,76.1%_97.7%,74.1%_44.1%)]" />
          </div>

          <div className="mx-auto max-w-5xl text-center">
            {/* Badge superior */}
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-electric/40 bg-electric/10 px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold text-cyan-300 mb-5 sm:mb-6 text-center">
              <Sparkles className="size-3.5 text-cyan-400 animate-pulse shrink-0" />
              <span className="truncate sm:whitespace-normal">Plataforma SaaS Multi-Vertical · En la Nube y Offline</span>
            </div>

            {/* Título Principal */}
            <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl text-white leading-tight">
              El sistema integral para{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric to-cyan-400">
                operar, vender y crecer
              </span>{" "}
              tu negocio.
            </h1>

            {/* Subtítulo */}
            <p className="mx-auto mt-4 sm:mt-6 max-w-3xl text-sm sm:text-base lg:text-lg text-white/70 leading-relaxed">
              Punto de venta ágil, comandas para restaurantes y órdenes de servicio para talleres.
              Control total de inventario en tiempo real, turnos de caja, compras y reportes de
              márgenes en una sola plataforma que nunca se detiene.
            </p>

            {/* CTAs */}
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-md mx-auto sm:max-w-none">
              <button
                type="button"
                onClick={handleLoginClick}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-msr bg-electric px-6 sm:px-8 py-3.5 sm:py-4 text-base font-bold text-white shadow-lg shadow-electric/30 hover:bg-electric-hover active:bg-electric-pressed transition-all transform hover:-translate-y-0.5"
              >
                <span>Acceder a mi Negocio</span>
                <ArrowRight className="size-5" />
              </button>

              <a
                href="#soluciones"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-msr border border-white/20 bg-white/5 px-6 py-3.5 sm:py-4 text-base font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Explorar Soluciones
              </a>
            </div>

            {/* Ventajas Rápidas */}
            <div className="mt-10 sm:mt-14 grid grid-cols-2 gap-3 sm:gap-4 border-t border-white/10 pt-6 sm:pt-8 sm:grid-cols-4 text-left">
              <div className="flex items-center gap-2 sm:gap-2.5">
                <CheckCircle2 className="size-4 sm:size-5 text-emerald-400 shrink-0" />
                <span className="text-[11px] sm:text-xs font-medium text-white/80">Cobro rápido en segundos</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5">
                <CheckCircle2 className="size-4 sm:size-5 text-cyan-400 shrink-0" />
                <span className="text-[11px] sm:text-xs font-medium text-white/80">Opera sin conexión (Offline)</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5">
                <CheckCircle2 className="size-4 sm:size-5 text-amber-400 shrink-0" />
                <span className="text-[11px] sm:text-xs font-medium text-white/80">Kardex y multialmacén</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5">
                <CheckCircle2 className="size-4 sm:size-5 text-indigo-400 shrink-0" />
                <span className="text-[11px] sm:text-xs font-medium text-white/80">Multi-sucursal y SaaS seguro</span>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------------- */}
        {/* Las 3 Soluciones Verticales */}
        {/* ------------------------------------------------------------------------- */}
        <section id="soluciones" className="border-t border-white/10 bg-ink-soft py-14 sm:py-20 px-4 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
              <span className="text-xs font-bold uppercase tracking-widest text-electric">
                Especializado por Industria
              </span>
              <h2 className="mt-2 font-display text-2xl sm:text-4xl font-bold text-white leading-tight">
                Diseñado para la forma real en que opera tu empresa
              </h2>
              <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-white/60">
                Cada negocio tiene dinámicas distintas. MegaSyR ofrece módulos especializados
                adaptados a cada rubro.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-3">
              {/* Vertical 1: Punto de Venta */}
              <div
                id="pos"
                className="group relative rounded-msr-lg border border-white/10 bg-white/5 p-6 sm:p-8 transition-all hover:border-emerald-500/50 hover:bg-white/[0.07]"
              >
                <div className="size-12 rounded-msr bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-5 sm:mb-6">
                  <LayoutGrid className="size-6" />
                </div>
                <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-2">
                  Punto de Venta & Retail
                </h3>
                <p className="text-xs sm:text-sm text-white/60 mb-5 sm:mb-6">
                  Venta ágil para tiendas, refaccionarias, minisúpers y comercios con mostrador de
                  atención rápida.
                </p>
                <ul className="space-y-2.5 text-xs text-white/80">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-emerald-400 shrink-0" />
                    <span>Venta por código de barras o búsqueda rápida</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-emerald-400 shrink-0" />
                    <span>Atajos de teclado completos para cobrar sin ratón</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-emerald-400 shrink-0" />
                    <span>Cobro en efectivo, tarjeta y divisas (USD/MXN)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-emerald-400 shrink-0" />
                    <span>Cotizaciones y tickets para impresoras térmicas</span>
                  </li>
                </ul>
              </div>

              {/* Vertical 2: Restaurante */}
              <div
                id="restaurante"
                className="group relative rounded-msr-lg border border-white/10 bg-white/5 p-6 sm:p-8 transition-all hover:border-amber-500/50 hover:bg-white/[0.07]"
              >
                <div className="size-12 rounded-msr bg-amber-500/10 text-amber-400 flex items-center justify-center mb-5 sm:mb-6">
                  <UtensilsCrossed className="size-6" />
                </div>
                <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-2">
                  Restaurantes & Cafeterías
                </h3>
                <p className="text-xs sm:text-sm text-white/60 mb-5 sm:mb-6">
                  Operación fluida entre sala, meseros, cocina y caja para alimentos y bebidas.
                </p>
                <ul className="space-y-2.5 text-xs text-white/80">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-amber-400 shrink-0" />
                    <span>Mapa gráfico de mesas por zonas (Salón, Terraza, Barra)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-amber-400 shrink-0" />
                    <span>Comandas digitales a pantalla de cocina (KDS)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-amber-400 shrink-0" />
                    <span>Cuentas abiertas, notas y división de tickets</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-amber-400 shrink-0" />
                    <span>Tiempos de preparación y alertas de retardo</span>
                  </li>
                </ul>
              </div>

              {/* Vertical 3: Taller */}
              <div
                id="taller"
                className="group relative rounded-msr-lg border border-white/10 bg-white/5 p-6 sm:p-8 transition-all hover:border-cyan-500/50 hover:bg-white/[0.07]"
              >
                <div className="size-12 rounded-msr bg-cyan-500/10 text-cyan-400 flex items-center justify-center mb-5 sm:mb-6">
                  <Wrench className="size-6" />
                </div>
                <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-2">
                  Talleres Mecánicos & Técnicos
                </h3>
                <p className="text-xs sm:text-sm text-white/60 mb-5 sm:mb-6">
                  Seguimiento meticuloso desde la recepción de la unidad hasta su reparación y
                  entrega final.
                </p>
                <ul className="space-y-2.5 text-xs text-white/80">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-cyan-400 shrink-0" />
                    <span>Órdenes de servicio con datos de vehículo o equipo</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-cyan-400 shrink-0" />
                    <span>Presupuesto y cotización de refacciones y mano de obra</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-cyan-400 shrink-0" />
                    <span>Asignación a mecánicos o técnicos responsables</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="size-3.5 text-cyan-400 shrink-0" />
                    <span>Historial de mantenimientos por cliente y unidad</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------------- */}
        {/* Módulos Transversales */}
        {/* ------------------------------------------------------------------------- */}
        <section id="modulos" className="py-14 sm:py-20 px-4 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
              <span className="text-xs font-bold uppercase tracking-widest text-electric">
                Potencia Operativa
              </span>
              <h2 className="mt-2 font-display text-2xl sm:text-4xl font-bold text-white leading-tight">
                Todo lo que tu administración necesita
              </h2>
              <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-white/60">
                Herramientas empresariales que acompañan a cada servicio para tener control total de
                tus recursos.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {/* Inventario */}
              <div className="rounded-msr-lg border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3">
                <div className="rounded-msr bg-electric/10 p-3 text-electric w-fit">
                  <Boxes className="size-5" />
                </div>
                <h4 className="font-display font-bold text-white text-base">
                  Inventario & Multialmacén
                </h4>
                <p className="text-xs text-white/60 leading-relaxed">
                  Kardex estricto e inmutable, valuación a costo promedio ponderado, traslados entre
                  sucursales, auditorías y alertas de stock en rojo.
                </p>
              </div>

              {/* Catálogo de Productos */}
              <div className="rounded-msr-lg border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3">
                <div className="rounded-msr bg-cyan-500/10 text-cyan-400 w-fit p-3">
                  <Tag className="size-5" />
                </div>
                <h4 className="font-display font-bold text-white text-base">
                  Catálogo de Productos & Servicios
                </h4>
                <p className="text-xs text-white/60 leading-relaxed">
                  Alta rápida de artículos, códigos de barras, presentaciones (SKU), categorías,
                  impuestos y listas de precios personalizadas.
                </p>
              </div>

              {/* Caja y Turnos */}
              <div className="rounded-msr-lg border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3">
                <div className="rounded-msr bg-emerald-500/10 text-emerald-400 w-fit p-3">
                  <Wallet className="size-5" />
                </div>
                <h4 className="font-display font-bold text-white text-base">
                  Caja & Turnos Ciegos
                </h4>
                <p className="text-xs text-white/60 leading-relaxed">
                  Apertura y cierre de turnos con arqueo ciego para evitar fraudes, retiros de
                  efectivo, gastos menores y conciliación al centavo.
                </p>
              </div>

              {/* Compras y Proveedores */}
              <div className="rounded-msr-lg border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3">
                <div className="rounded-msr bg-amber-500/10 text-amber-400 w-fit p-3">
                  <Truck className="size-5" />
                </div>
                <h4 className="font-display font-bold text-white text-base">
                  Compras & Proveedores
                </h4>
                <p className="text-xs text-white/60 leading-relaxed">
                  Órdenes de compra, recepción de mercancía directamente a existencias y gestión de
                  cuentas por pagar a proveedores.
                </p>
              </div>

              {/* Clientes */}
              <div className="rounded-msr-lg border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3">
                <div className="rounded-msr bg-indigo-500/10 text-indigo-400 w-fit p-3">
                  <Users className="size-5" />
                </div>
                <h4 className="font-display font-bold text-white text-base">
                  Clientes & Cuentas por Cobrar
                </h4>
                <p className="text-xs text-white/60 leading-relaxed">
                  Directorio centralizado con datos fiscales (RFC), historial detallado de compras,
                  crédito en tienda y cotizaciones.
                </p>
              </div>

              {/* Reportes */}
              <div className="rounded-msr-lg border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3">
                <div className="rounded-msr bg-rose-500/10 text-rose-400 w-fit p-3">
                  <Receipt className="size-5" />
                </div>
                <h4 className="font-display font-bold text-white text-base">
                  Reportes & Analítica de Rentabilidad
                </h4>
                <p className="text-xs text-white/60 leading-relaxed">
                  Cálculo de márgenes brutos reales, artículos más vendidos, cortes de turno,
                  exportación a Excel y métricas operativas al día.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------------- */}
        {/* Tecnología Offline */}
        {/* ------------------------------------------------------------------------- */}
        <section
          id="offline"
          className="border-t border-white/10 bg-gradient-to-b from-ink-soft to-carbon py-14 sm:py-20 px-4 sm:px-6"
        >
          <div className="mx-auto max-w-5xl rounded-msr-lg border border-white/15 bg-white/[0.03] p-6 sm:p-10 md:p-12">
            <div className="flex flex-col sm:flex-row items-center sm:items-start md:items-center gap-6 sm:gap-8 text-center sm:text-left">
              <div className="size-16 sm:size-20 shrink-0 rounded-2xl bg-electric/20 border border-electric/40 text-cyan-300 flex items-center justify-center">
                <WifiOff className="size-8 sm:size-10" />
              </div>

              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-cyan-500/10 px-3 py-1 text-xs font-semibold text-cyan-300">
                  <RefreshCw className="size-3 animate-spin" />
                  <span>Sincronización Automática</span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">
                  ¿Se cayó el internet? En MegaSyR nunca te detienes.
                </h3>
                <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                  A diferencia de otros sistemas que se congelan cuando falla el proveedor de
                  internet, MegaSyR está construido con arquitectura <strong>Offline-First</strong>.
                  Tus cajeros y meseros pueden seguir cobrando e imprimiendo normalmente. Cuando la
                  conexión vuelve, los datos se sincronizan solos sin duplicar cobros.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------------- */}
        {/* Seguridad y Multi-inquilino */}
        {/* ------------------------------------------------------------------------- */}
        <section id="seguridad" className="py-12 sm:py-16 px-4 sm:px-6 border-t border-white/10">
          <div className="mx-auto max-w-4xl text-center space-y-4">
            <div className="size-12 rounded-full bg-emerald-500/10 text-emerald-400 mx-auto flex items-center justify-center">
              <ShieldCheck className="size-6" />
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-bold text-white">
              Seguridad de nivel corporativo para tu negocio
            </h3>
            <p className="text-xs sm:text-sm text-white/60 leading-relaxed max-w-2xl mx-auto">
              Cada negocio cuenta con políticas de aislamiento de datos estrictas (Row-Level Security
              en PostgreSQL), contraseñas cifradas con Argon2id, registros de auditoría y sesiones
              con rotación automática de tokens.
            </p>
          </div>
        </section>

        {/* ------------------------------------------------------------------------- */}
        {/* Sección: Prueba Gratuita */}
        {/* ------------------------------------------------------------------------- */}
        <section id="prueba-gratis" className="border-t border-white/10 bg-gradient-to-b from-carbon via-electric/10 to-carbon py-14 sm:py-20 px-4 sm:px-6">
          <div className="mx-auto max-w-4xl text-center space-y-5 sm:space-y-6">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3.5 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold text-cyan-300">
              <Gift className="size-3.5 sm:size-4 text-cyan-400 shrink-0" />
              <span>Prueba 15 Días Sin Costo · Sin Tarjeta de Crédito</span>
            </div>

            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              ¿Listo para transformar tu negocio?{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric to-cyan-400">
                Pide tu cuenta de prueba
              </span>
            </h2>

            <p className="text-sm sm:text-base lg:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              Te configuramos un entorno completo adaptado a tu giro (Punto de Venta, Restaurante o Taller).
              Comprueba de primera mano la velocidad del cobro offline, el control de inventario y los reportes
              en tu propia sucursal antes de contratar.
            </p>

            <div className="pt-2 sm:pt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-md mx-auto sm:max-w-none">
              <Button
                variant="primary"
                size="lg"
                onClick={() => void navigate({ to: "/contacto" })}
                className="w-full sm:w-auto flex items-center justify-center gap-2 shadow-xl shadow-electric/30 text-sm sm:text-base px-6 sm:px-8 py-3.5"
              >
                <Sparkles className="size-4 sm:size-5" />
                <span>Solicitar Prueba Gratuita</span>
              </Button>

              <a
                href="https://wa.me/528672131335?text=Hola,%20me%20gustar%C3%ADa%20solicitar%20mi%20cuenta%20de%20prueba%20gratis%20de%20MegaSyR"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-msr border border-emerald-500/40 bg-emerald-500/10 px-5 sm:px-6 py-3 text-xs sm:text-sm font-semibold text-emerald-400 hover:bg-emerald-500/20 transition-colors"
              >
                <MessageSquare className="size-4" />
                <span>Pedir por WhatsApp (867 213 1335)</span>
              </a>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------------- */}
        {/* Sección: Contacto y Soporte */}
        {/* ------------------------------------------------------------------------- */}
        <section id="contacto" className="border-t border-white/10 bg-ink-soft py-14 sm:py-20 px-4 sm:px-6">
          <div className="mx-auto max-w-6xl space-y-10 sm:space-y-12">
            <div className="text-center max-w-3xl mx-auto space-y-3 sm:space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-semibold text-white/80">
                <Headphones className="size-3.5 text-electric" />
                <span>Atención Personalizada y Soporte Técnico</span>
              </div>
              <h2 className="font-display text-2xl sm:text-4xl font-bold text-white leading-tight">
                Estamos listos para atenderte
              </h2>
              <p className="text-xs sm:text-base text-white/70">
                Atención directa, asesoría para elegir el paquete ideal y resolución de dudas técnicas.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {/* WhatsApp */}
              <a
                href="https://wa.me/528672131335?text=Hola,%20quisiera%20informaci%C3%B3n%20sobre%20el%20sistema%20MegaSyR"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3 hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-all group"
              >
                <div className="size-11 sm:size-12 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <MessageSquare className="size-5 sm:size-6" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-emerald-400">WhatsApp Directo</h3>
                <p className="text-base sm:text-lg font-bold text-white">867 213 1335</p>
                <p className="text-xs text-white/60">
                  Escríbenos para dudas rápidas, cotizaciones o demostraciones en tiempo real.
                </p>
              </a>

              {/* Teléfono */}
              <a
                href="tel:+528672131335"
                className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all group"
              >
                <div className="size-11 sm:size-12 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="size-5 sm:size-6" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-cyan-400">Teléfono</h3>
                <p className="text-base sm:text-lg font-bold text-white">(867) 213-1335</p>
                <p className="text-xs text-white/60">
                  Llamada telefónica directa de lunes a sábado de 8:00 AM a 8:00 PM.
                </p>
              </a>

              {/* Correo */}
              <a
                href="mailto:contacto@megasyr.mx"
                className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 space-y-3 hover:border-indigo-500/50 hover:bg-indigo-500/10 transition-all group sm:col-span-2 md:col-span-1"
              >
                <div className="size-11 sm:size-12 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="size-5 sm:size-6" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-indigo-400">Correo Electrónico</h3>
                <p className="text-base sm:text-lg font-bold text-white break-all sm:break-normal">contacto@megasyr.mx</p>
                <p className="text-xs text-white/60">
                  Solicitudes formales, información de planes, cuentas corporativas y facturación.
                </p>
              </a>
            </div>

            {/* Ubicación y Cobertura */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-5 sm:gap-6">
              <div className="flex items-start sm:items-center gap-3.5 sm:gap-4 text-left">
                <div className="size-11 sm:size-12 rounded-xl bg-white/10 text-white flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
                  <MapPin className="size-5 sm:size-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-bold text-white">Atención directa y soporte técnico: Tlalixcoyan, Veracruz</p>
                  <p className="text-xs text-white/60 mt-0.5">
                    Soporte presencial en sitio y asistencia remota garantizada a negocios en todo México.
                  </p>
                </div>
              </div>

              <Button
                variant="outline"
                size="md"
                onClick={() => void navigate({ to: "/contacto" })}
                className="w-full sm:w-auto shrink-0 flex items-center justify-center gap-2 py-3"
              >
                <span>Ir al Formulario de Contacto</span>
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------------- */}
        {/* CTA Final */}
        {/* ------------------------------------------------------------------------- */}
        <section className="border-t border-white/10 bg-electric/10 py-12 sm:py-16 px-4 sm:px-6 text-center">
          <div className="mx-auto max-w-3xl space-y-4 sm:space-y-6">
            <h2 className="font-display text-2xl sm:text-4xl font-bold text-white leading-tight">
              Comienza a operar tu negocio con MegaSyR
            </h2>
            <p className="text-xs sm:text-sm text-white/70 max-w-xl mx-auto leading-relaxed">
              Accede a tu cuenta de negocio o solicita la habilitación de los servicios de Punto de
              Venta, Restaurante o Taller para tu empresa.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-md mx-auto sm:max-w-none">
              <Button
                variant="primary"
                size="lg"
                onClick={handleLoginClick}
                className="w-full sm:w-auto flex items-center justify-center gap-2 shadow-lg shadow-electric/30 px-6 sm:px-8 py-3.5 text-sm sm:text-base font-bold"
              >
                <LogIn className="size-4 sm:size-5" />
                <span>Iniciar Sesión en el Sistema</span>
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => void navigate({ to: "/contacto" })}
                className="w-full sm:w-auto flex items-center justify-center gap-2 border-white/20 text-white hover:bg-white/10 text-sm sm:text-base py-3.5"
              >
                <Gift className="size-4 sm:size-5 text-cyan-400" />
                <span>Pedir Cuenta de Prueba</span>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* ------------------------------------------------------------------------- */}
      {/* Footer */}
      {/* ------------------------------------------------------------------------- */}
      <footer className="border-t border-white/10 bg-ink py-8 px-4 sm:px-6 text-xs text-white/40">
        <div className="mx-auto max-w-7xl flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo variant="isotipo" tone="white" className="h-6" />
            <span className="text-center sm:text-left">MegaSyR SaaS Platform © {new Date().getFullYear()} · Todos los derechos reservados</span>
          </div>

          <div className="text-center md:text-right space-y-1.5">
            <p className="leading-relaxed">
              Atención directa y soporte: <strong>Tlalixcoyan, Veracruz</strong> · WhatsApp:{" "}
              <a href="https://wa.me/528672131335" className="text-white hover:underline font-bold">
                (867) 213-1335
              </a>{" "}
              ·{" "}
              <a href="mailto:contacto@megasyr.mx" className="text-white hover:underline font-bold">
                contacto@megasyr.mx
              </a>
            </p>
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-3 sm:gap-4 pt-1">
              <button
                type="button"
                onClick={() => void navigate({ to: "/contacto" })}
                className="text-white/60 hover:text-white transition-colors"
              >
                Contacto y Prueba Gratis
              </button>
              <button
                type="button"
                onClick={handleLoginClick}
                className="text-white/60 hover:text-white transition-colors"
              >
                Acceso a Clientes
              </button>
              <span>v0.6.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
